import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  InputAdornment,
  IconButton,
  Switch
} from '@mui/material';
import { Add as AddIcon, ArrowBack as ArrowBackIcon, Visibility, VisibilityOff } from '@mui/icons-material';

import userService from '../../services/user.service';

// Phone number validation regex
const phoneRegExp = /^\+?[0-9]{10,15}$/;

// Validation schema
const UserSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string().min(8, 'Password must be at least 8 characters').required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Confirm password is required'),
  msisdn: Yup.string().matches(phoneRegExp, 'Phone number is not valid').nullable(),
  role: Yup.string().required('Role is required'),
  is_active: Yup.boolean()
});

function UserCreate() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const initialValues = {
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    msisdn: '',
    role: 'user',
    is_active: true
  };

  const handleSubmit = async (values, { resetForm }) => {
    setLoading(true);
    setError(null);

    // Remove confirmPassword field for API submission
    const { confirmPassword, ...userDataToSubmit } = values;

    try {
      await userService.createUser(userDataToSubmit);
      navigate('/users');
    } catch (err) {
      console.error('Failed to create user:', err);
      setError(err.message || 'Failed to create user. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/users')}
          sx={{ mb: 2 }}
        >
          Back to Users
        </Button>
        <Typography variant="h4" component="h1" gutterBottom>
          Create User
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Add a new admin user or participant
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <CardContent>
          <Formik
            initialValues={initialValues}
            validationSchema={UserSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, values, setFieldValue }) => (
              <Form>
                <Grid container spacing={4}>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Basic Information
                    </Typography>
                    <Divider sx={{ mb: 3 }} />

                    <Field
                      as={TextField}
                      name="name"
                      label="Full Name"
                      fullWidth
                      margin="normal"
                      variant="outlined"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                    />

                    <Field
                      as={TextField}
                      name="email"
                      label="Email Address"
                      fullWidth
                      margin="normal"
                      variant="outlined"
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                    />

                    <Field
                      as={TextField}
                      name="msisdn"
                      label="Phone Number"
                      fullWidth
                      margin="normal"
                      variant="outlined"
                      placeholder="+233..."
                      error={touched.msisdn && Boolean(errors.msisdn)}
                      helperText={touched.msisdn && errors.msisdn}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Security & Access
                    </Typography>
                    <Divider sx={{ mb: 3 }} />

                    <Field
                      as={TextField}
                      name="password"
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      fullWidth
                      margin="normal"
                      variant="outlined"
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />

                    <Field
                      as={TextField}
                      name="confirmPassword"
                      label="Confirm Password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      fullWidth
                      margin="normal"
                      variant="outlined"
                      error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                      helperText={touched.confirmPassword && errors.confirmPassword}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              edge="end"
                            >
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />

                    <Box sx={{ mt: 2 }}>
                      <FormControl component="fieldset" sx={{ mb: 2 }}>
                        <FormLabel component="legend">User Role</FormLabel>
                        <RadioGroup
                          row
                          name="role"
                          value={values.role}
                          onChange={(e) => setFieldValue('role', e.target.value)}
                        >
                          <FormControlLabel value="admin" control={<Radio />} label="Admin" />
                          <FormControlLabel value="user" control={<Radio />} label="Regular User" />
                        </RadioGroup>
                      </FormControl>
                    </Box>

                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                      <Switch
                        checked={values.is_active}
                        onChange={(e) => setFieldValue('is_active', e.target.checked)}
                        color="success"
                      />
                      <Typography>Active Account</Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/users')}
                    sx={{ mr: 2 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <AddIcon />}
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Create User'}
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </Container>
  );
}

export default UserCreate;